import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { type Comment, Discussion } from "components/lib/Discussion/Discussion";
import type { CommentMedia } from "components/lib/Discussion/types";

import type {
  ProjectBoardCommentUpdateBody,
  ProjectCommentDraft,
  ProjectCommentMedia,
  ProjectCommentReplyDrafts,
} from "./types";

type IProjectComments = {
  forIdea: string;
  forId: string;
  canPost: boolean;
  canManage: boolean;
};

const ProjectComments = ({
  forIdea,
  forId,
  canPost,
  canManage,
}: IProjectComments) => {
  const user = useAppSelector((state) => state.user);

  const { t } = useTranslation();

  const [commentLoading, setCommentLoading] = useState(false);

  const [newComment, setNewComment] = useState<ProjectCommentDraft>({
    text: "",
    media: [],
  });
  const [replies, setReplies] = useState<ProjectCommentReplyDrafts>({});

  const [commentsState, setCommentsState] = useState({
    page: 1,
    limit: 15,
    nextPageAvailable: false,
    previousPageAvailable: false,
    total: 0,
  });

  const { page, limit, nextPageAvailable, previousPageAvailable, total } =
    commentsState;

  const commentQuery = useQuery(
    initUseQuery(
      api.boards.getProjectComments,
      [forId, forIdea, { page, limit }],
      { isEnabled: !!user && !!forId && !!forIdea },
    ),
  );

  const onPageChange = useCallback((newPage: number) => {
    setCommentsState((prevState) => ({ ...prevState, page: newPage }));
  }, []);

  const postComment = useCallback(
    (parent: string | undefined = "") => {
      const draft = parent ? replies[parent] : newComment;
      const text = draft?.text ?? "";
      if (!text) return;

      const media = draft?.media
        ?.filter(
          (
            item,
          ): item is ProjectCommentMedia & { value: string; name: string } =>
            !!item.value && !!item.name,
        )
        .map((item) => ({
          value: item.value,
          name: item.name,
          type: item.type as "file" | "image" | "gif",
        }));

      setCommentLoading(true);
      api.boards.postProjectComment(
        forId,
        forIdea,
        {
          message: { text, ...(media?.length ? { media } : {}) },
          ...(parent ? { parent } : {}),
        },
        () => {
          toast.success(t("ideas.comments.posted"));
          if (parent) {
            setReplies((prev) => {
              const newState = { ...prev };
              delete newState[parent];
              return newState;
            });
            setCommentLoading(false);
            api.invalidateQueries(api.boards.getProjectComments, [
              forId,
              forIdea,
              { page, limit },
            ]);
          } else {
            setNewComment({ text: "", media: [] });
            setCommentsState((prevState) => ({ ...prevState, page: 1 }));
            setCommentLoading(false);
            api.invalidateQueries(api.boards.getProjectComments, [
              forId,
              forIdea,
              { page, limit },
            ]);
          }
          api.setQueryData(api.challenges.getIdeas, [forId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((i) => {
              if (i._id !== forIdea) return i;
              return { ...i, updatedAt: new Date().toISOString() };
            }),
          }));
        },
        (err) => toast.error(err.message),
      );
    },
    [newComment, replies, t, forIdea, forId, page, limit],
  );

  const deleteComment = useCallback(
    (commentId: string) =>
      util
        .confirm(
          t("ideas.comments.delete.title"),
          t("ideas.comments.delete.info"),
        )
        .then(() => {
          api.boards.deleteProjectComment(
            forId,
            forIdea,
            commentId,
            () => {
              api.setQueryData(
                api.boards.getProjectComments,
                [forId, forIdea, { page, limit }],
                (prev) => ({
                  ...prev,
                  comments: prev.comments.filter((c) => c._id !== commentId),
                }),
              );
              toast(t("ideas.comments.delete.success"));
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {}),
    [forIdea, t, forId, page, limit],
  );

  const addAttachment = useCallback(
    (
      storedName: string,
      attachmentName: string,
      signedDownloadRequest: string,
      parentId?: string,
    ) => {
      const attachment: ProjectCommentMedia = {
        value: storedName,
        name: attachmentName,
        signedDownloadRequest,
        type: attachmentName.match(/\.(jpg)|(png)|(gif)|(jpeg)$/i)
          ? "image"
          : "file",
      };
      if (parentId) {
        setReplies((prev) => ({
          ...prev,
          [parentId]: {
            text: prev[parentId]?.text ?? "",
            media: [...(prev[parentId]?.media ?? []), attachment],
          },
        }));
      } else {
        setNewComment((prev) => ({
          ...prev,
          media: [...prev.media, attachment],
        }));
      }
    },
    [],
  );

  const updateProjectComment = useCallback(
    (commentId: string, data: ProjectBoardCommentUpdateBody) => {
      api.boards.updateProjectComment(
        forId,
        forIdea,
        commentId,
        data,
        () => {
          api.invalidateQueries(api.boards.getProjectComments, [
            forId,
            forIdea,
          ]);
        },
        (err) => toast.error(err.message),
      );
    },
    [forIdea, forId],
  );

  const deleteAttachment = useCallback((index: number, parentId?: string) => {
    if (parentId) {
      setReplies((prev) => ({
        ...prev,
        [parentId]: {
          text: prev[parentId]?.text ?? "",
          media: (prev[parentId]?.media ?? []).filter((_, i) => i !== index),
        },
      }));
    } else {
      setNewComment((prev) => ({
        ...prev,
        media: prev.media.filter((_, i) => i !== index),
      }));
    }
  }, []);

  const getFile = useCallback(
    (att: ProjectCommentMedia) => att.signedDownloadRequest || att.value || "",
    [],
  );

  const noop = useCallback(() => {}, []);

  return (
    <div style={{ marginTop: 15 }}>
      <h5 style={{ marginBottom: 0 }}>Project notes</h5>
      <p style={{ color: "grey" }}>
        These notes are only visible to admins and users with access to the
        project board.
      </p>
      <Discussion
        post={() => postComment()}
        deleteComment={deleteComment}
        pin={(commentId) => updateProjectComment(commentId, { isPinned: true })}
        unpin={(commentId) =>
          updateProjectComment(commentId, { isPinned: false })
        }
        feed={(commentQuery.data?.comments ?? []) as Comment[]}
        loading={commentQuery.isLoading}
        commentLoading={commentLoading}
        canPost={
          canPost ||
          util.hasPermission(user, "challenge.createProjectNotes", forId)
        }
        canManage={canManage}
        approveComment={noop}
        rejectComment={noop}
        reactComment={noop}
        postReply={(parent) => postComment(parent)}
        onPageChange={onPageChange}
        updateComment={(text) => setNewComment((prev) => ({ ...prev, text }))}
        updateReply={(commentId, text) => {
          setReplies((prev) => ({
            ...prev,
            [commentId]: { text, media: prev[commentId]?.media ?? [] },
          }));
        }}
        getReplyData={(commentId) => {
          const reply = replies[commentId] ?? { text: "", media: [] };
          return { text: reply.text, media: reply.media as Comment["media"] };
        }}
        commentText={newComment.text}
        commentMedia={newComment.media}
        page={page}
        limit={limit}
        nextPageAvailable={nextPageAvailable}
        previousPageAvailable={previousPageAvailable}
        total={total}
        disableReacts
        addAttachment={addAttachment}
        deleteAttachment={deleteAttachment}
        getFile={(media: CommentMedia) =>
          getFile({
            value: media.value,
            name: media.name,
            signedDownloadRequest: media.signedDownloadRequest,
            type: media.type ?? "file",
          })
        }
        fileChooserForType="idea"
        fileChooserForId={forIdea}
        postText="Post note"
        placeholder="Enter a note.."
        type="projectComment"
        canPin={
          util.hasPermission(user, "challenge.pinProjectNotes", forId) ||
          canManage
        }
        disableAttachments={false}
        noCommentsText="No notes yet"
      />
    </div>
  );
};

export default ProjectComments;
